import { evaluateSpotlight } from '../utils/spotlight.js';
import { getScreen, setScreen } from './aiCache.js';

/**
 * Hintergrund-Screener — findet automatisch die „coolsten“ Flugzeuge im Radius.
 *
 * Token-sparsam in drei Stufen:
 *   1. Geräte-/Server-Cache (getScreen) — schon klassifiziert? fertig.
 *   2. Kostenlose Heuristik (utils/spotlight.js) — Rettung/Polizei/Militär/selten
 *      sofort und gratis; offensichtlich Gewöhnliches wird als solches abgehakt.
 *   3. Nur der UNSICHERE Rest geht gebündelt (≤ BATCH_LIMIT) an das billige
 *      Haiku-Modell (/ai/screen).
 *
 * Läuft automatisch bei jeder Datenaktualisierung (entprellt). Ist das Backend
 * nicht erreichbar (z. B. noch nicht deployed), bleiben die Heuristik-Funde
 * trotzdem sichtbar; die KI-Abfragen pausieren kurz und werden später wiederholt.
 */

const AI_BASE = '/ai';
const DEBOUNCE_MS = 1200;
const BATCH_LIMIT = 20;
const FAIL_COOLDOWN_MS = 60000;

export class AiScreener {
  constructor(flightStore, spotlightState) {
    this.store = flightStore;
    this.spotlight = spotlightState;
    this.screened = new Set(); // hex bereits klassifiziert (Cache/Heuristik/KI)
    this.pendingAi = new Set(); // hex gerade im KI-Batch unterwegs
    this.timer = null;
    this.aiCooldownUntil = 0;
    this.enabled = true;

    this.store.on('update', () => this._schedule());
  }

  _schedule() {
    if (!this.enabled || this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this._tick();
    }, DEBOUNCE_MS);
  }

  async _tick() {
    const batch = [];

    for (const flight of this.store.getAllFlights()) {
      const hex = flight.id;
      if (!hex || hex.startsWith('demo') || flight.gone || flight.onGround) continue;
      if (this.screened.has(hex) || this.pendingAi.has(hex)) continue;

      // 1) Bereits gecacht (Gerät/Server)?
      const cached = await getScreen(hex);
      if (cached) {
        this.screened.add(hex);
        if (cached.interesting) this.spotlight.set(hex, cached);
        continue;
      }

      // 2) Kostenlose Heuristik
      const verdict = evaluateSpotlight(flight);
      if (verdict) {
        this.screened.add(hex);
        setScreen(hex, verdict);
        if (verdict.interesting) this.spotlight.set(hex, verdict);
        continue;
      }

      // 3) Unsicher → für den KI-Batch vormerken
      batch.push(flight);
    }

    if (batch.length && Date.now() >= this.aiCooldownUntil) {
      await this._screenWithAi(batch.slice(0, BATCH_LIMIT));
    }
  }

  async _screenWithAi(flights) {
    const hexes = flights.map((f) => f.id);
    hexes.forEach((h) => this.pendingAi.add(h));

    try {
      const res = await fetch(`${AI_BASE}/screen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          aircraft: flights.map((f) => ({
            hex: f.id,
            callsign: f.callsign || null,
            registration: f.registration || null,
            type: f.aircraftType || null,
            category: f.category || null,
            squawk: f.squawk || null
          }))
        })
      });
      if (!res.ok) throw new Error(`screen ${res.status}`);
      const json = await res.json();
      const verdicts = Array.isArray(json?.verdicts) ? json.verdicts : [];
      const byHex = new Map(verdicts.map((v) => [(v.hex || '').toLowerCase(), v]));

      for (const hex of hexes) {
        const verdict = byHex.get(hex.toLowerCase()) || { interesting: false, source: 'ai' };
        this.screened.add(hex);
        setScreen(hex, verdict);
        if (verdict.interesting) this.spotlight.set(hex, verdict);
      }
    } catch {
      // Backend nicht erreichbar → kurz zurückhalten (Heuristik-Funde bleiben
      // sichtbar), die vorgemerkten Hexes werden nach der Cooldown erneut geprüft.
      this.aiCooldownUntil = Date.now() + FAIL_COOLDOWN_MS;
    } finally {
      hexes.forEach((h) => this.pendingAi.delete(h));
    }
  }
}
